/**
@format
@flow
*/
import React from 'react';
import styled from 'styled-components';
import type { ButtonPrimary as ButtonPrimaryType, ButtonIcon as ButtonIconType, ButtonGroupData as ButtonData } from '@ziynx/flow-types/buttons';
import Button from './Button';
import { getDimensions } from '../utilities';

type Props = $ReadOnly<{|
  /**
   * Data object to render the buttons individually.
  */
  data?: Array<ButtonPrimaryType> | Array<ButtonIconType>,
  /**
   * Data object to render the buttons in rows.
   * Used at the stacked viewport.
   * Can override the default button group stacking pattern.
  */
  rowData?: Array<any>,
  /**
   * View of the button group. Row or column
   * Defaults to row
   */
  stacked?: boolean,
  /**
   * Custom render function for items
  */
  renderItem?: () => void,
  /**
   * Set width of button group
  */
  width?: string,
|}>;

const _getButtonMargin = (stacked, useRows, lastChild) => {
  if ((stacked && useRows) || !stacked) return lastChild ? null : 'margin-right: 12px';
  return 'margin-bottom: 16px;';
};

const Wrapper = styled.div`
  ${({ stacked }) => (!stacked
    ? `
    display: flex;
   flex-direction: row;`
    : `
    display: block;
  `)}
  min-width: ${({ stacked }) => (!stacked ? '348px' : '100%')};
  ${({ width, stacked }) => width && !stacked && `width: ${width};`};
`;

const _calcWidth = (rowQuantity) => {
  const percentage = 100 / rowQuantity;
  const margin = 12 * (rowQuantity - 1);
  return `calc(${(percentage || 50)}% - ${(margin || 12)}px)`;
};

const StyledButtonIcon = styled(Button)`
${({ lastChild, stacked }) => !stacked && !lastChild && 'margin-right: 12px;'};
${({ lastChild, stacked }) => stacked && !lastChild && 'margin-right: 16px;'};
${({ stacked, rowQuantity }) => stacked && `
  min-width: auto;
  max-width: none;
  width: ${rowQuantity ? _calcWidth(rowQuantity) : 'calc(50% - 8px)'};
  padding: 0;
  padding-top: 12px;
  padding-bottom: 12px;
`};`;

const StyledButtonPrimary = styled(Button)`
  ${({ stacked, useRows, lastChild }) => _getButtonMargin(stacked, useRows, lastChild)};
`;

const RowWrapper = styled.div`
    display: flex;
    ${({ lastChild }) => !lastChild && 'margin-bottom: 16px;'};
`;

/**
 * ButtonGroup - layouts for buttonPrimary and two buttonIcons
 */
const ButtonGroup = (props: Props): React$Node => {
  const {
    data,
    rowData,
    stacked,
    renderItem,
    width,
    ...rest
  } = props;

  if (!data) return null;

  /* Button Group Standard */
  // eslint-disable-next-line max-len
  const _renderItem = (item: ButtonData, index: number, rowQuantity): React$Node => {
    const keyIndex = `item-${index}`;
    // eslint-disable-next-line max-len
    const lastChild = (rowData && typeof rowQuantity === 'number') ? (index === rowQuantity - 1) : index === data?.length - 1;
    // $FlowFixMe   to stop flow error for href and title
    const { variant, href, title } = item || {};
    switch (variant) {
    case 'icon':
      return (
        <StyledButtonIcon
          {...item}
          anchorWidth={stacked ? 'auto' : undefined}
          rowQuantity={rowData ? rowQuantity : undefined}
          isLink={!href}
          stacked={stacked}
          href={href}
          key={keyIndex}
          variant="icon"
          lastChild={lastChild}
          useRows={!!rowData}
        />
      );
    case 'primary':
    default:
      return (
        <StyledButtonPrimary
          {...item}
          key={keyIndex}
          variant="primary"
          stacked={stacked}
          width={!stacked ? '168px' : '100%'}
          lastChild={lastChild}
          useRows={!!rowData}
        >
          {title}
        </StyledButtonPrimary>
      );
    }
  };

  const _renderRows = (): React$Node => {
    if (!rowData || !rowData.length) return null;
    /* Map the rows, then the data in the rows */
    return rowData.map((row, rowIndex) => {
      if (!row || !row.length) return null;
      const rowQuantity = row.length || 2;
      return (
        <RowWrapper
          key={rowIndex}
          lastChild={rowIndex === rowData?.length - 1}
        >
          {row && row?.map((item: ButtonData, index) => (
            item ? _renderItem(item, index, rowQuantity) : null))}
        </RowWrapper>
      );
    });
  };

  const BUTTON_WIDTH = getDimensions(width);

  return (
    <Wrapper
      width={BUTTON_WIDTH}
      stacked={stacked}
      useRows={!!rowData}
      {...rest}
    >
      {stacked && !!rowData && _renderRows()}
      {(!stacked || !rowData) && data.map(_renderItem)}
    </Wrapper>
  );
};

ButtonGroup.defaultProps = {
  stacked: false,
  width: undefined,
};

export default ButtonGroup;
